import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, Loader2 } from 'lucide-react';
import { PermissionEditor } from '../PermissionEditor';
import { useCheckRoleIdUnique } from '../../hooks/useRoles';
import type { Permission, CreateRoleInput } from '../../types';

/**
 * Validate that a permission string has a valid format
 */
function isValidPermission(permission: Permission): boolean {
  if (!permission.k || !permission.v) return false;

  const parts = permission.k.split('.');
  if (parts.length < 2) return false;

  const modelPart = parts[0].replace(/\[[^\]]*\]$/, '');
  if (!modelPart || !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(modelPart)) return false;

  const validActions = ['read', 'create', 'modify', 'delete'];
  if (!validActions.includes(parts[1])) return false;

  return true;
}

interface CreateRoleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: CreateRoleInput) => Promise<void>;
  isSubmitting?: boolean;
}

export function CreateRoleModal({
  open,
  onOpenChange,
  onSave,
  isSubmitting,
}: CreateRoleModalProps) {
  const { t } = useTranslation('roles');
  const [roleId, setRoleId] = useState('');
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [roleIdError, setRoleIdError] = useState<string | null>(null);
  const [permissionsError, setPermissionsError] = useState<string | null>(null);

  const checkUniqueMutation = useCheckRoleIdUnique();

  const resetForm = () => {
    setRoleId('');
    setPermissions([]);
    setRoleIdError(null);
    setPermissionsError(null);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetForm();
    }
    onOpenChange(newOpen);
  };

  const validateRoleId = async (): Promise<boolean> => {
    if (!roleId.trim()) {
      setRoleIdError(t('form.roleIdRequired'));
      return false;
    }

    const isUnique = await checkUniqueMutation.mutateAsync({ roleId });
    if (!isUnique) {
      setRoleIdError(t('form.roleIdNotUnique'));
      return false;
    }

    setRoleIdError(null);
    return true;
  };

  const validatePermissions = (): boolean => {
    const invalidPermissions = permissions.filter((p) => !isValidPermission(p));
    if (invalidPermissions.length > 0) {
      setPermissionsError(t('form.invalidPermissions', 'Some permissions have invalid format'));
      return false;
    }
    setPermissionsError(null);
    return true;
  };

  const handleSave = async () => {
    const isRoleIdValid = await validateRoleId();
    const isPermissionsValid = validatePermissions();

    if (!isRoleIdValid || !isPermissionsValid) return;

    await onSave({
      roleId,
      permissions,
    });

    resetForm();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('modal.createTitle')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Role ID */}
          <div className="space-y-2">
            <Label htmlFor="roleId">{t('form.roleId')}</Label>
            <Input
              id="roleId"
              value={roleId}
              onChange={(e) => {
                setRoleId(e.target.value);
                setRoleIdError(null);
              }}
              placeholder={t('form.roleIdPlaceholder')}
              disabled={isSubmitting}
            />
            {roleIdError && (
              <div className="flex items-center gap-1 text-sm text-red-600">
                <AlertCircle className="h-4 w-4" />
                {roleIdError}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              {t('form.roleIdHint', 'Choose a descriptive name for this role. It will be used to assign permissions to users.')}
            </p>
          </div>

          {/* Permissions */}
          <PermissionEditor
            permissions={permissions}
            onChange={(newPerms) => {
              setPermissions(newPerms);
              setPermissionsError(null);
            }}
            disabled={isSubmitting}
          />
          {permissionsError && (
            <div className="flex items-center gap-1 text-sm text-red-600">
              <AlertCircle className="h-4 w-4" />
              {permissionsError}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSubmitting}
          >
            {t('actions.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={isSubmitting || !roleId.trim()}>
            {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t('actions.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
